import { defineStore } from "pinia";
import {
  BillableUsageStatus,
  getBillablePeriods,
  type BillablePeriodItem,
  type BillableUsageExportValue,
  type BillableUsageResponse,
} from "@coronet/cui";
import { StoreModule } from "@/constants/store.ts";
import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";
import { i18n } from "@/plugins/i18n.ts";
import type { DataValue } from "@/types.ts";

export interface MspBillableUsageState {
  workspaceId: string;
  period: string;
  usage: DataValue<BillableUsageResponse>;
  /** The month list runs up to this moment (ms); moved on whenever a default month is resolved. */
  periodsUntil: number;
  /** Status of the month picked in the export dialog. */
  checkedPeriodStatus: DataValue<BillablePeriodStatus>;
}

/** Status of a month, and whether it has any billable usage (a calculated month can have none). */
export type BillablePeriodStatus = Pick<BillableUsageResponse, "status" | "hasUsage">;

export interface BillableUsageExportDialogItem {
  workspaceId: string;
  period: string;
  periods: BillablePeriodItem[];
}

async function requestBillableUsage(
  workspaceId: string,
  period: string,
  statusOnly = false
): Promise<BillableUsageResponse> {
  const { data } = await axiosInstance.request<BillableUsageResponse>({
    ...api.mspBillableUsage(workspaceId),
    // axios leaves undefined params out of the query, so the full request sends no statusOnly.
    params: { period, statusOnly: statusOnly || undefined },
  });
  return data;
}

/** Status of a month; undefined when the request failed (logged, without a toast). */
async function requestPeriodStatus(
  workspaceId: string,
  period: string
): Promise<BillablePeriodStatus | undefined> {
  try {
    const { status, hasUsage } = await requestBillableUsage(workspaceId, period, true);
    return { status, hasUsage };
  } catch (error) {
    console.error("Failed to check the billing usage month", error);
    return undefined;
  }
}

export const useMspBillableUsageStore = defineStore(StoreModule.MSP_BILLABLE_USAGE, {
  state: (): MspBillableUsageState => ({
    workspaceId: "",
    period: "",
    usage: { loading: false },
    periodsUntil: Date.now(),
    checkedPeriodStatus: { loading: false },
  }),
  getters: {
    periods: (state): BillablePeriodItem[] =>
      getBillablePeriods({ locale: i18n.global.locale.value, to: new Date(state.periodsUntil) }),
  },
  actions: {
    /** Status of the month picked in the export dialog. A failed check leaves `error`; the month stays exportable. */
    async checkPeriodStatus(workspaceId: string, period: string) {
      // The widget has this month loaded already: no request. A full answer has no `hasUsage`; its items tell.
      if (this.workspaceId === workspaceId && this.usage.data?.period === period) {
        const { status, items } = this.usage.data;
        this.checkedPeriodStatus = { loading: false, data: { status, hasUsage: !!items?.length } };
        return;
      }
      this.checkedPeriodStatus = { loading: true };
      const periodStatus = await requestPeriodStatus(workspaceId, period);
      this.checkedPeriodStatus = periodStatus
        ? { loading: false, data: periodStatus }
        : { loading: false, error: true };
    },

    /**
     * Newest calculated month, walking back from the current one; else the current month.
     * A calculated month without billable usage counts too: it is the latest billing result.
     * The walk stops at the first failed request: the backend is unlikely to answer the next month.
     */
    async resolveDefaultPeriod(workspaceId: string): Promise<string> {
      this.periodsUntil = Date.now();
      const periods = this.periods.map((item) => item.value);
      for (const period of periods) {
        const periodStatus = await requestPeriodStatus(workspaceId, period);
        if (periodStatus?.status === BillableUsageStatus.CALCULATED) return period;
        if (!periodStatus) break;
      }
      return periods[0];
    },

    async loadBillableUsage(workspaceId: string) {
      this.workspaceId = workspaceId;
      this.period = "";
      this.usage = { loading: true };
      const period = await this.resolveDefaultPeriod(workspaceId);
      await this.getBillableUsage(workspaceId, period);
    },

    async getBillableUsage(workspaceId: string, period: string) {
      this.period = period;
      this.usage = { loading: true };
      try {
        const data = await requestBillableUsage(workspaceId, period);
        this.usage = { loading: false, data };
      } catch (error) {
        console.error("Failed to load billable usage", error);
        this.usage = { loading: false, error: true };
      }
    },

    /** Starts the CSV export; false when the backend refused it (the axios interceptor shows why). */
    async exportBillingUsage(
      payload: BillableUsageExportValue & { workspaceId: string }
    ): Promise<boolean> {
      try {
        await axiosInstance.request({ ...api.mspExportBillingUsage(), data: payload });
        return true;
      } catch (error) {
        console.error("Failed to export billing usage", error);
        return false;
      }
    },
  },
});
